/* --------------------------------------------------------------------------------------------------------------
   CART — CHECKOUT FOCUS  (A/B: MC_CART_CHECKOUTFOCUS_LB24)

   Drives the scroll-progress rail under the recommendations carousel. The design has one; the theme does not.

   Deliberately small and defensive:

   - It reads the carousel's live scroll position rather than any server-rendered attribute. The carousel is
     horizontally scrollable and its scrollWidth changes with the number of recommendations, so a value baked in at
     render time would be wrong the moment anything reflows.
   - It re-measures on resize, because scrollWidth/clientWidth both move with the viewport.
   - It respects the kill switch on every frame rather than caching the flag once: the A/B tool may add or remove
     `ccf24-off` after this file has run, and a captured boolean would go stale. When the variant is off the rail is
     display:none anyway, so the work stops at one class check.
   - It never throws when the carousel is absent — the recommendations block only renders when a cart item actually
     carries recommended products.
   -------------------------------------------------------------------------------------------------------------- */

(function () {
  'use strict';

  var MIN_THUMB = 24; // a thumb narrower than this reads as a dot rather than a position

  function isOff() {
    return document.documentElement.classList.contains('ccf24-off') ||
           document.body.classList.contains('ccf24-off');
  }

  function paint(rail, carousel) {
    if (isOff()) return;

    var track = rail.clientWidth;
    var visible = carousel.clientWidth;
    var total = carousel.scrollWidth;
    var thumb = rail.querySelector('.ccf24-rail__thumb');
    if (!thumb || !track || !total) return;

    // Nothing to scroll: the rail would be a full-width bar claiming there is more to see.
    if (total - visible < 1) {
      rail.hidden = true;
      return;
    }
    rail.hidden = false;

    var width = Math.max(MIN_THUMB, Math.round(track * (visible / total)));
    var travel = track - width;
    var progress = carousel.scrollLeft / (total - visible);

    // The carousel is scrollable in both directions under RTL, where scrollLeft goes negative.
    progress = Math.min(1, Math.max(0, Math.abs(progress)));

    thumb.style.width = width + 'px';
    thumb.style.transform = 'translateX(' + Math.round(travel * progress) + 'px)';
  }

  function wire(rail) {
    var carousel = document.getElementById(rail.getAttribute('data-ccf24-rail-for'));
    if (!carousel || rail.dataset.ccf24RailWired === 'true') return;
    rail.dataset.ccf24RailWired = 'true';

    var queued = false;
    var update = function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        paint(rail, carousel);
      });
    };

    carousel.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    // The product images load after this runs and change scrollWidth as they do.
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(update).observe(carousel);
    }

    update();
  }

  /* ------------------------------------------------------------------------------------------------------------
     Express checkout buttons: one row instead of a column.

     Shopify's accelerated checkout component reads `additional-checkout-buttons--vertical` off the wrapper and
     stacks its wallet buttons when it is present. Nothing in page CSS can undo that afterwards — the layout is the
     component's own, and six different CSS strategies were measured against the deployed theme with selectors that
     outranked everything else on the page: all six left the buttons stacked at full width.

     Proof it is the class and not something else: this store's other cart section renders the same three wallets
     without the modifier and gets a row. Removing the class here before hydration produced 3 x 150px on one line.

     Done in JS rather than in the Liquid so the CONTROL keeps the class and stays exactly as it is today. The
     component reads the class once, early, so:
       - this runs from a deferred script, i.e. during readyState "interactive", which measured early enough;
       - a short bounded watchdog covers the case where the block is rendered later than that.

     One-way by nature: switching the variant off after the page has loaded will not put the buttons back into a
     column, because the component has already laid itself out. The A/B tool sets its class before load, so this
     only matters when toggling by hand in a console.
     ------------------------------------------------------------------------------------------------------------ */
  function unstackExpressButtons() {
    if (isOff()) return false;
    var stacked = document.querySelectorAll('.cart .additional-checkout-buttons--vertical');
    for (var i = 0; i < stacked.length; i++) {
      stacked[i].classList.remove('additional-checkout-buttons--vertical');
    }
    return stacked.length > 0;
  }

  function watchExpressButtons() {
    if (unstackExpressButtons()) return;
    var n = 0;
    var timer = setInterval(function () {
      if (unstackExpressButtons() || ++n >= 25) clearInterval(timer);
    }, 200);
  }

  /* ------------------------------------------------------------------------------------------------------------
     Page dots — the mobile counterpart of the rail.

     One dot per page rather than per product: the carousel scrolls by viewport, so a page is what a swipe moves.
     The count therefore depends on the live clientWidth/scrollWidth and cannot be rendered server-side.

     No cap on the count. At the widest this cart gets today that is 23 dots, which at 4px plus a 4px gap still fits
     one line inside the container — capping would misreport how much is left to scroll.
     ------------------------------------------------------------------------------------------------------------ */
  function paintDots(host, carousel) {
    if (isOff()) return;

    var visible = carousel.clientWidth;
    var total = carousel.scrollWidth;
    if (!visible || !total) return;

    var pages = Math.max(1, Math.round(total / visible));
    if (pages < 2) {
      host.hidden = true;
      return;
    }
    host.hidden = false;

    if (host.children.length !== pages) {
      host.textContent = '';
      for (var i = 0; i < pages; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ccf24-dots__dot';
        dot.setAttribute('aria-label', String(i + 1));
        dot.dataset.ccf24Page = String(i);
        host.appendChild(dot);
      }
    }

    var active = Math.min(pages - 1, Math.round(Math.abs(carousel.scrollLeft) / visible));
    for (var j = 0; j < host.children.length; j++) {
      host.children[j].classList.toggle('is-active', j === active);
    }
  }

  function wireDots(host) {
    var carousel = document.getElementById(host.getAttribute('data-ccf24-dots-for'));
    if (!carousel || host.dataset.ccf24DotsWired === 'true') return;
    host.dataset.ccf24DotsWired = 'true';

    var queued = false;
    var update = function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        paintDots(host, carousel);
      });
    };

    host.addEventListener('click', function (event) {
      var dot = event.target.closest('.ccf24-dots__dot');
      if (!dot) return;
      carousel.scrollTo({ left: carousel.clientWidth * Number(dot.dataset.ccf24Page), behavior: 'smooth' });
    });

    carousel.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    if (typeof ResizeObserver === 'function') new ResizeObserver(update).observe(carousel);

    update();
  }

  /* ------------------------------------------------------------------------------------------------------------
     Line-item price colour.

     This one is done from script, which normally would be the wrong tool. The reasoning, measured on the deployed
     theme rather than assumed:

       - the stylesheet rule for `.line-item__info .price-list sale-price` is live and Element.matches confirms it
         selects the price, yet the price computed rgb(227,79,79);
       - adding !important to it changed nothing;
       - injecting `html body .cart … sale-price { color: … !important }` at runtime ALSO changed nothing;
       - setting the colour inline with priority "important" did work.

     Walking every readable stylesheet found no color declaration matching the element, and the three sheets script
     cannot read are Google Fonts and two from the Essential Pre-Order app, neither of which mentions sale-price
     when fetched directly. So the source stays unidentified — but the experiment above pins down exactly which
     tier still wins, and inline-with-important is the only one available.

     Reversible: with .ccf24-off the property is removed again, so the control keeps the theme's own colour.
     ------------------------------------------------------------------------------------------------------------ */
  var PRICE_SELECTOR = '.cart .order-summary__body .line-item__info .price-list sale-price';

  function paintPrices() {
    var off = isOff();
    var prices = document.querySelectorAll(PRICE_SELECTOR);
    for (var i = 0; i < prices.length; i++) {
      if (off) prices[i].style.removeProperty('color');
      else prices[i].style.setProperty('color', '#272727', 'important');
    }
  }

  function watchPrices() {
    paintPrices();

    // Observing .order-summary__body was wrong: an ajax cart replaces that whole subtree, which takes the observer
    // with it and loses the repaint. The observed root has to be a node that survives — see CLAUDE.md on ajax
    // sections being rebuilt wholesale. document.body is the only one guaranteed to.
    if (typeof MutationObserver === 'function') {
      new MutationObserver(paintPrices).observe(document.body, { childList: true, subtree: true });
    }

    // Belt and braces for the first paint: if the section is still being assembled when this runs, the observer
    // above catches it, but a short bounded poll covers the case where the swap happens without a mutation this
    // observer sees. Bounded on purpose — an endless timer for a colour is wasted work on every visit.
    var tries = 0;
    var timer = setInterval(function () {
      paintPrices();
      if (++tries >= 10) clearInterval(timer);
    }, 300);
  }

  function init() {
    watchPrices();

    unstackExpressButtons();
    watchExpressButtons();

    var rails = document.querySelectorAll('[data-ccf24-rail-for]');
    for (var i = 0; i < rails.length; i++) wire(rails[i]);

    var dots = document.querySelectorAll('[data-ccf24-dots-for]');
    for (var d = 0; d < dots.length; d++) wireDots(dots[d]);
  }

  // Ahead of DOMContentLoaded: the wrapper is in the server HTML, so it exists as soon as this deferred script runs.
  unstackExpressButtons();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
